import { useCallback, useMemo } from 'react'
import { useApp } from '@/store/AppContext'
import { routeConversationMessage } from '@/services/conversationRuntime'
import { classifyAgentIntent } from '@/lib/agentIntent'
import type { AgentMode } from '@/hooks/useAgent'
import type { ConversationMode } from '@/types/conversationRuntime'
import type { MessageAttachment, WorkspaceSurface } from '@/types'
import type { RuntimeFallbackTarget } from '@/lib/workspace'
import { filterReadyConnectorIds } from '@/shared/connectors/availability'

type SendChatMessage = (
  text: string,
  planningMode?: boolean,
  studioMode?: boolean,
  assistantRole?: 'assistant' | 'agent',
  runtimeSurface?: WorkspaceSurface,
  attachments?: MessageAttachment[],
  backendInputOverride?: string,
) => void | Promise<void>
type StartAgentRun = (task: string, mode?: AgentMode, studioMode?: boolean, runtimeTask?: string) => void | Promise<void>
type StopRuntime = () => void | Promise<void>

interface UseConversationRuntimeOptions {
  sendMessage: SendChatMessage
  startAgent: StartAgentRun
  stopStreaming: StopRuntime
  stopAgent: StopRuntime
  agentMode: AgentMode
  defaultRouteOverride?: ConversationMode
  fallbackTarget: RuntimeFallbackTarget
  runtimeSurface: WorkspaceSurface
  isAgentActive: boolean
  appMode: 'chat' | 'studio'
  canStartAgent: boolean
  onNoCredits: () => void
  onAgentLimit: () => void
  onBeforeSend: (text: string) => void
  onStartChat: () => void
  onStartAgent: () => void
  setAppMode: (mode: 'chat' | 'studio') => void
}

interface SendConversationOptions {
  mode?: AgentMode
  overrideMode?: ConversationMode
  attachments?: MessageAttachment[]
  backendMessage?: string
}

const toRuntimeHistory = (messages: NonNullable<ReturnType<typeof useApp>['activeConversation']>['messages']) =>
  messages
    .filter(message => message.role === 'user' || message.role === 'assistant' || message.role === 'agent')
    .filter(message => message.content.trim() !== '')
    .slice(-16)
    .map(message => ({
      role: message.role,
      content: message.content,
    }))

const isShortCorrection = (text: string) => {
  const normalized = text.trim().toLowerCase()
  if (!/^(nei|ikke|jo|mente|kun|bare)\b/.test(normalized)) return false
  return normalized.split(/\s+/).filter(Boolean).length <= 10
}

const fallbackTargetForMessage = (text: string, configuredFallback: RuntimeFallbackTarget): RuntimeFallbackTarget => {
  const localIntent = classifyAgentIntent(text)
  if (localIntent === 'full_agent') return 'agent'
  return configuredFallback
}

const lastMeaningfulUserMessage = (messages: NonNullable<ReturnType<typeof useApp>['activeConversation']>['messages']) => {
  const userMessages = messages
    .filter(message => message.role === 'user')
    .map(message => message.content.trim())
    .filter(Boolean)
  return userMessages[userMessages.length - 1] ?? ''
}

const contextualAgentTask = (
  text: string,
  messages: NonNullable<ReturnType<typeof useApp>['activeConversation']>['messages'] | undefined,
) => {
  if (!messages || !isShortCorrection(text)) return text
  const previousUserGoal = lastMeaningfulUserMessage(messages)
  if (!previousUserGoal) return text
  const correction = text.trim().toLowerCase()
  const previous = previousUserGoal.trim()
  const looksLikeHomeValueCorrection = /\b(verdi|boligverdi|pris|estimat)\b/.test(correction)
    && /\b(bolig|leilighet|eiendom|artillerivollen|adresse|kvadrat|kvm|m2|m²|pris)\b/i.test(previous)
  if (looksLikeHomeValueCorrection) {
    return [
      'Fortsett den forrige boligoppgaven. Bruk korrigeringen som en presisering, ikke som en ny uklar oppgave.',
      `Forrige brukeroppgave: ${previous}`,
      `Korrigering: ${text}`,
      'Bruk all boligkontekst fra forrige run aktivt.',
      'Målet er kun verdi/verdiestimat for boligen. Ikke lag et steg som avklarer hva "verdi" betyr.',
      'Hvis eksakt verdi ikke kan hentes, gi beste trygge estimat/intervall basert på tilgjengelige salgsdata, prisantydning, område og tydelige forbehold.',
    ].join('\n')
  }
  return [
    'Fortsett den forrige brukeroppgaven med denne korrigeringen.',
    `Forrige brukeroppgave: ${previousUserGoal}`,
    `Korrigering: ${text}`,
    'Bruk forrige kontekst aktivt. Ikke spør hva brukeren mener hvis korrigeringen kan forstås fra forrige oppgave.',
  ].join('\n')
}

export function useConversationRuntime({
  sendMessage,
  startAgent,
  stopStreaming,
  stopAgent,
  agentMode,
  defaultRouteOverride,
  fallbackTarget,
  runtimeSurface,
  isAgentActive,
  appMode,
  canStartAgent,
  onNoCredits,
  onAgentLimit,
  onBeforeSend,
  onStartChat,
  onStartAgent,
  setAppMode,
}: UseConversationRuntimeOptions) {
  const { activeConversation, settings } = useApp()

  const sendConversationMessage = useCallback(async (
    text: string,
    hasNoCredits: boolean,
    options: SendConversationOptions = {},
  ) => {
    onBeforeSend(text)

    if (hasNoCredits) {
      onNoCredits()
      return
    }

    const isPlanningMode = text.startsWith('[PLANLEGGINGSMODUS]')
    const routeOverride = options.overrideMode ?? defaultRouteOverride
    const activeConnectors = filterReadyConnectorIds(
      activeConversation?.enabledConnectors ?? [],
      settings.connectorCapabilities ?? {},
      settings.connectorStatuses ?? {},
      settings.connectorDisabled ?? {},
    )

    let routeResult: Awaited<ReturnType<typeof routeConversationMessage>> | null = null
    try {
      routeResult = await routeConversationMessage({
        message: text,
        conversationId: activeConversation?.id,
        conversationHistory: activeConversation ? toRuntimeHistory(activeConversation.messages) : [],
        overrideMode: routeOverride,
        studioMode: appMode === 'studio',
        enabledConnectors: activeConnectors,
      })
    } catch {
      routeResult = null
    }

    const decision = routeResult?.decision
    const routeFallbackTarget = fallbackTargetForMessage(text, fallbackTarget)
    const shouldStartAgent = decision
      ? decision.legacy_target === 'agent' || (decision.legacy_target === 'chat' && routeFallbackTarget === 'agent')
      : routeFallbackTarget === 'agent'
    const shouldUseStudio = appMode === 'studio' || decision?.mode === 'studio_build'

    if (decision?.mode === 'studio_build' && appMode !== 'studio') {
      setAppMode('studio')
    }

    if (!shouldStartAgent) {
      onStartChat()
      await sendMessage(
        text,
        isPlanningMode,
        shouldUseStudio,
        'assistant',
        runtimeSurface,
        options.attachments,
        options.backendMessage,
      )
      return
    }

    if (!canStartAgent) {
      onAgentLimit()
      return
    }

    onStartAgent()
    await startAgent(
      text,
      options.mode ?? agentMode,
      shouldUseStudio,
      options.backendMessage ?? contextualAgentTask(text, activeConversation?.messages),
    )
  }, [
    activeConversation,
    agentMode,
    appMode,
    canStartAgent,
    defaultRouteOverride,
    fallbackTarget,
    onAgentLimit,
    onBeforeSend,
    onNoCredits,
    onStartAgent,
    onStartChat,
    runtimeSurface,
    sendMessage,
    setAppMode,
    startAgent,
    settings.connectorCapabilities,
    settings.connectorDisabled,
    settings.connectorStatuses,
  ])

  const sendSuggestion = useCallback((text: string, hasNoCredits: boolean) => {
    void sendConversationMessage(text, hasNoCredits)
  }, [sendConversationMessage])

  const regenerateLast = useCallback((hasNoCredits: boolean) => {
    if (!activeConversation) return
    const lastUserMsg = [...activeConversation.messages].reverse().find(message => message.role === 'user')
    if (lastUserMsg) {
      void sendConversationMessage(lastUserMsg.content, hasNoCredits)
    }
  }, [activeConversation, sendConversationMessage])

  const stopConversationRun = useCallback(() => {
    if (isAgentActive) {
      void stopAgent()
      return
    }
    void stopStreaming()
  }, [isAgentActive, stopAgent, stopStreaming])

  return useMemo(() => ({
    sendConversationMessage,
    sendSuggestion,
    regenerateLast,
    stopConversationRun,
  }), [regenerateLast, sendConversationMessage, sendSuggestion, stopConversationRun])
}
